// Toast store — the SINGLE source of truth for the toast stack.
//
// Sprint UI-9 bridged this store to `sonner`, which left the product with TWO
// stores: this zustand array AND sonner's own internal one, kept in sync only
// through sonner's `onDismiss`/`onAutoClose` callbacks. DS.8 (campaign
// aos-design-system, spec §2 D5) collapses that: `push()` no longer calls
// `sonnerToast`, the array is the only source, and `ds/DsToaster.tsx` renders
// it through `@ds/feedback/Toast`'s `ToastContainer`.
//
// PUBLIC API UNCHANGED. `push` / `dismiss` / `dismissByKey` / `clear` and the
// exported `Toast` / `ToastAction` interfaces are byte-compatible, so all 13
// caller files / 39 invocation sites compile and behave untouched.
//
// ── D5.1, THE DISMISSAL ASYMMETRY — an INVARIANT, not an implementation
// detail. It existed before this sprint and it survives it:
//
//   EXPLICIT removal  (`dismiss(id)`, `dismissByKey(key)`, and the DS Toast's
//                      close ✕ / Escape, which route into `dismiss(id)`)
//                      -> fires the caller's `onDismiss`.
//   AUTO-EXPIRY       (the `durationMs` timer)
//                      -> fires NOTHING. The record is dropped silently.
//
// The live consumer of the difference is `useSocket.ts`'s notification toast:
// its `onDismiss` calls `useNotificationsStore.markRead(id)`, a PERSISTED,
// CROSS-WINDOW-BROADCAST state change. An explicit dismissal IS a read; a
// five-second expiry is not. Getting this wrong empties the founder's unread
// badge while he is not looking. `web/tests/Toast.test.tsx`'s last case is the
// guard, and `web/tests/ds8-toaster.test.tsx` duplicates it at the rendered
// layer so the invariant survives a future rewrite of either file.
//
// Why the store owns the timer instead of the DS piece: `@ds/feedback/Toast`
// exposes ONE `onClose`, fired identically by its internal `setTimeout`, by
// its close ✕ and by Escape [medido: Toast.tsx at the pinned v0.2.0]. Mapping
// `ToastContainer.onRemove -> dismiss(id)` with the piece's timer armed would
// therefore route every expiry into `markRead`. `DsToaster` passes
// `duration={0}` to disarm the piece; the timer below replaces it.
//
// `durationMs: 0` means STICKY — no timer at all, the old `Infinity`
// semantics. `clear()` fires nothing and has zero production callers.

import { create } from 'zustand';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface Toast {
  id: string;
  message: string;
  action?: ToastAction;
  durationMs: number;
  createdAt: number;
  key?: string;
}

interface ToastStoreState {
  toasts: Toast[];
  push: (input: {
    message: string;
    action?: ToastAction;
    durationMs?: number;
    key?: string;
    onDismiss?: () => void;
  }) => string;
  dismiss: (id: string) => void;
  dismissByKey: (key: string) => void;
  clear: () => void;
}

const DEFAULT_DURATION_MS = 5_000;

/**
 * The caller's `onDismiss`, and the expiry timer, kept OUTSIDE the zustand
 * state on purpose: neither is renderable, and putting a function or a timer
 * handle in the store would make every subscriber re-render on a change no
 * subscriber can read. `push()` used to close over `onDismiss` inside sonner's
 * callback; now the store has to keep it itself.
 */
const onDismissById = new Map<string, () => void>();
const timerById = new Map<string, ReturnType<typeof setTimeout>>();

function forget(id: string): void {
  const timer = timerById.get(id);
  if (timer !== undefined) clearTimeout(timer);
  timerById.delete(id);
  onDismissById.delete(id);
}

function randomId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `toast-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export const useToastStore = create<ToastStoreState>((set) => ({
  toasts: [],
  push: ({ message, action, durationMs = DEFAULT_DURATION_MS, key, onDismiss }) => {
    const id = randomId();
    const toastRecord: Toast = {
      id,
      message,
      action,
      durationMs,
      createdAt: Date.now(),
      key,
    };

    if (onDismiss) onDismissById.set(id, onDismiss);

    set((s) => ({ toasts: [...s.toasts, toastRecord] }));

    // durationMs === 0 -> sticky, no timer (the old `Infinity`). The expiry
    // path drops the record WITHOUT running the caller's onDismiss (D5.1).
    if (durationMs > 0) {
      timerById.set(
        id,
        setTimeout(() => {
          forget(id);
          set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
        }, durationMs),
      );
    }

    return id;
  },
  dismiss: (id) => {
    // Explicit dismissal. Read the callback BEFORE `forget` drops it, and run
    // it before the state update so a handler that re-enters the store (it
    // may: markRead broadcasts) sees the record already accounted for.
    const onDismiss = onDismissById.get(id);
    forget(id);
    onDismiss?.();
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
  },
  dismissByKey: (key) => {
    const matches = useToastStore.getState().toasts.filter((t) => t.key === key);
    const callbacks = matches.map((t) => onDismissById.get(t.id));
    for (const t of matches) forget(t.id);
    for (const cb of callbacks) cb?.();
    set((s) => ({ toasts: s.toasts.filter((t) => t.key !== key) }));
  },
  clear: () => {
    for (const id of [...timerById.keys(), ...onDismissById.keys()]) forget(id);
    set({ toasts: [] });
  },
}));
